import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useBulkUpdateMutation } from '@/hooks/agent/useBulkUpdate';
import { BulkUpdateService } from '@/services/agent/bulk/BulkUpdateService';
import { BulkUpdateJobTarget, PendingBulkUpdate } from '@/types/agent/bulk-update';
import { PHONE_GOALS_TEMPLATE_ID, SILENCE_TIMEOUT_TEMPLATE_ID } from './BulkUpdateTemplates';

interface ConfirmBulkUpdateDialogProps {
  isOpen: boolean;
  selectedAgentIds: Set<string>;
  pending: PendingBulkUpdate | null;
  onClose: () => void;
  onJobCreated?: (jobId: number) => void;
  onQueuedJobCreated?: () => void;
}

export const ConfirmBulkUpdateDialog = ({
  isOpen,
  selectedAgentIds,
  pending,
  onClose,
  onJobCreated,
  onQueuedJobCreated,
}: ConfirmBulkUpdateDialogProps) => {
  const queryClient = useQueryClient();
  const { mutate: bulkUpdate, isPending: isUpdatingSelected } = useBulkUpdateMutation();

  const isSilenceTimeout = pending?.templateId === SILENCE_TIMEOUT_TEMPLATE_ID;
  const isPhoneGoals = pending?.templateId === PHONE_GOALS_TEMPLATE_ID;
  const isQueued = isSilenceTimeout || isPhoneGoals || !!pending?.scope.applyToAll;
  const target: BulkUpdateJobTarget = pending?.scope.applyToAll
    ? { includeDemo: pending.scope.includeDemo, includeMarketplace: pending.scope.includeMarketplace }
    : { agentIds: Array.from(selectedAgentIds) };

  const { data: queuedCount, isLoading: isCounting, error: countError } = useQuery({
    queryKey: ['bulk-update-count', pending?.templateId, target],
    queryFn: () => {
      if (isSilenceTimeout) return BulkUpdateService.previewSilenceTimeout(target);
      if (isPhoneGoals) return BulkUpdateService.previewPhoneGoals(target);
      return BulkUpdateService.previewBulkUpdateAll(target);
    },
    enabled: isOpen && isQueued,
    staleTime: 0,
  });

  const { mutate: startQueuedJob, isPending: isStartingJob } = useMutation({
    mutationFn: (request: { templateId?: string; target: BulkUpdateJobTarget; params: Record<string, any> }) => {
      if (request.templateId === SILENCE_TIMEOUT_TEMPLATE_ID) return BulkUpdateService.applySilenceTimeout(request.target);
      if (request.templateId === PHONE_GOALS_TEMPLATE_ID) return BulkUpdateService.applyPhoneGoals(request.target);
      return BulkUpdateService.bulkUpdateAll(request.target, request.params);
    },
    onSuccess: ({ genieCount }) => {
      queryClient.invalidateQueries({ queryKey: ['bulk-update-jobs'] });
      toast.success(
        <span>
          Updating {genieCount} {genieCount === 1 ? 'genie' : 'genies'}.{' '}
          <Link to="/admin/bulk-update-jobs" className="underline">Track progress</Link>
        </span>,
      );
      onQueuedJobCreated?.();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const isPending = isUpdatingSelected || isStartingJob;
  const count = isQueued ? queuedCount ?? 0 : selectedAgentIds.size;
  const isBlocked = isQueued && (isCounting || !!countError || count === 0);

  const handleConfirm = () => {
    if (!pending) return;

    if (isQueued) {
      startQueuedJob({ templateId: pending.templateId, target, params: pending.updates });
      return;
    }

    if (selectedAgentIds.size === 0) return;

    bulkUpdate(
      {
        agentIds: Array.from(selectedAgentIds),
        params: pending.updates,
      },
      {
        onSuccess: (data) => {
          onJobCreated?.(data.jobId);
          onClose();
        },
      }
    );
  };

  const getDescription = () => {
    if (countError) return (countError as Error).message;
    if (isQueued && isCounting) return 'Counting genies...';
    if (isQueued && count === 0) return 'No genies need this update.';
    const audience = pending?.scope.applyToAll ? ", including customers' genies" : '';
    return `You are about to update ${count} agent${count !== 1 ? 's' : ''}${audience}. Their current settings are saved first, so you can undo this from Bulk Update Jobs.`;
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent className="z-[999]" style={{ zIndex: 9999 }}>
        <AlertDialogTitle>Confirm Bulk Update</AlertDialogTitle>
        <AlertDialogDescription>{getDescription()}</AlertDialogDescription>
        <div className="flex gap-3 justify-end">
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isPending || isBlocked}
            className="bg-genie-primary hover:bg-genie-primary/90"
          >
            {isPending ? 'Confirming...' : 'Confirm Update'}
          </AlertDialogAction>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
};
